import { z } from 'zod';
import { schemaDebug } from '../../../utils/schemaLogger';
import { EmvDataSchema, ReceiptDataSchema, ReceiptObjectDataSchema } from '../common/Receipt';

/** Settlement status on the wire — free-form string (processor-defined values). */
const SetlStatSchema = z.string();

// Date format schemas
const AuthDateSchema = z.string()
  .regex(/^\d{8}$/, { message: "Auth date must be in YYYYMMDD format" })
  .optional();

const FullDateTimeSchema = z.string()
  .regex(/^\d{14}$/, { message: "Date time must be in YYYYMMDDHHMMSS format" })
  .optional();

const ExpirySchema = z.string()
  .regex(/^\d{4}$/, { message: "Expiry must be in MMYY format" })
  .optional();

// Amount and currency schemas
const AmountSchema = z.string()
  .max(12, { message: "Amount cannot exceed 12 characters" })
  .regex(/^\d+(\.\d{1,2})?$/, { message: "Amount must be a valid decimal number" })
  .optional();

const CurrencySchema = z.string()
  .length(3, { message: "Currency must be exactly 3 characters" })
  .regex(/^[A-Z]{3}$/, { message: "Currency must be 3 uppercase letters" })
  .optional();

// Response status enums
const RespStatSchema = z.enum(['A', 'B', 'C']).optional();

const VoidableSchema = z.enum(['Y', 'N']).optional();

const RefundableSchema = z.enum(['Y', 'N']).optional();

const DeclineCategorySchema = z.enum(['1', '2', '3', '4']).optional();

// Fee format enum
const FeeFormatSchema = z.enum(['flat', 'percent']).optional();

// BIN type - The wire can carry various values, so we accept any string
const BinTypeSchema = z.string().optional();

// Entry mode enum
const EntryModeSchema = z.enum([
  'Keyed', 'Moto', 'ECommerce', 'Recurring', 'Swipe (Non EMV)',
  'Digital Wallet', 'EMVContact', 'Contactless', 'Fallback to Track', 'Fallback to Keyed'
]).optional();

// Fee type enum
const FeeTypeSchema = z.enum([
  'CVNFEE', 'SURCHRG', 'SRVCFEE', 'SRVC_WAIVED',
  'CVN_WAIVED', 'SURCHRG_WAIVED', 'FEE_TXN'
]).optional();

// String field schemas with length limits
const MerchidSchema = z.string().max(16).optional();
const OrderidSchema = z.string().max(50).optional();
const RetrefSchema = z.string().max(32).optional();
const RespcodeSchema = z.string().max(3).optional();
const RespprocSchema = z.string().max(4).optional();
const ResptextSchema = z.string().max(40).optional();
const BatchidSchema = z.string().max(12).optional();
const SignatureSchema = z.string().max(6144).optional();
const TokenSchema = z.string().max(19).optional();
const ProfileidSchema = z.string().max(20).optional();
const AcctidSchema = z.string().max(3).optional();
const LastfourSchema = z.string().max(4).optional();
const NameSchema = z.string().max(30).optional();
const FeeAuthcodeSchema = z.string().max(6).optional();
const FeeMerchidSchema = z.string().max(16).optional();
const FeeRetrefSchema = z.string().optional();
const FeeValueSchema = z.string().optional();
const MerchAdviceCodeSchema = z.string().max(2).optional();

// JSON field schemas
const UserFieldsSchema = z.union([
  z.string().transform((str) => {
    try {
      return JSON.parse(str);
    } catch {
      return [];
    }
  }),
  z.array(z.record(z.string(), z.any())),
  z.record(z.string(), z.any())
]).optional();

const BaseInquireResponseSchema = z.object({
  // Basic transaction information
  merchid: MerchidSchema,
  orderid: OrderidSchema,
  expiry: ExpirySchema,
  amount: AmountSchema,
  currency: CurrencySchema,
  retref: RetrefSchema,

  // Response codes and status
  respcode: RespcodeSchema,
  respproc: RespprocSchema,
  respstat: RespStatSchema,
  resptext: ResptextSchema,

  // Settlement and batch information
  batchid: BatchidSchema,
  setlstat: SetlStatSchema,
  capturedate: FullDateTimeSchema,
  settledate: FullDateTimeSchema,

  // Card and account information
  signature: SignatureSchema,
  token: TokenSchema,
  account: z.string().max(27).optional(), // Masked account number or token
  authcode: z.union([
    z.string().max(6),
    z.number().int().positive()
  ]).optional(), // Authorization code
  profileid: ProfileidSchema,
  acctid: AcctidSchema,
  authdate: AuthDateSchema,
  lastfour: LastfourSchema,
  name: NameSchema,
  bintype: BinTypeSchema,
  entrymode: EntryModeSchema,
  cof: z.enum(['C', 'M']).optional(), // Card on file type

  // Fee information
  fee_authcode: FeeAuthcodeSchema,
  fee_format: FeeFormatSchema,
  fee_merchid: FeeMerchidSchema,
  fee_retref: FeeRetrefSchema,
  fee_type: FeeTypeSchema,
  fee_value: FeeValueSchema,

  // Complex JSON fields - accept both string (from API) and object (already parsed)
  emvTagData: z.union([
    z.string().transform((str): z.infer<typeof EmvDataSchema> | undefined => {
      // The wire may carry emvTagData as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = EmvDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof EmvDataSchema>;
      } catch {
        schemaDebug('Failed to parse emvTagData JSON, returning undefined');
        return undefined;
      }
    }),
    EmvDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),
  receipt: z.union([
    z.string().transform((str): z.infer<typeof ReceiptDataSchema> | undefined => {
      // The wire may carry receipt as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = ReceiptDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptDataSchema>;
      } catch {
        schemaDebug('Failed to parse receipt JSON, returning undefined');
        return undefined;
      }
    }),
    ReceiptDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),
  receiptObj: z.any().transform((value): z.infer<typeof ReceiptObjectDataSchema> | undefined => {
    // Handle undefined/null
    if (value === undefined || value === null) {
      return undefined;
    }

    // If it's already an object, validate and return it
    if (typeof value === 'object') {
      const validated = ReceiptObjectDataSchema.safeParse(value);
      return validated.success ? validated.data : value as z.infer<typeof ReceiptObjectDataSchema>;
    }

    // If it's a string, parse it as JSON
    if (typeof value === 'string') {
      if (value.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(value);
        const validated = ReceiptObjectDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptObjectDataSchema>;
      } catch {
        schemaDebug('Failed to parse receiptObj JSON, returning undefined');
        return undefined;
      }
    }

    // Otherwise return undefined
    return undefined;
  }).optional(),
  userfields: UserFieldsSchema,

  // Transaction capabilities
  voidable: VoidableSchema,
  refundable: RefundableSchema,

  // Decline information
  declineCategory: DeclineCategorySchema,
  declineCategoryText: z.string().optional(),
  merchAdviceCode: MerchAdviceCodeSchema,
  merchAdviceText: z.string().optional()
}).partial().strict();

const InquireResponseSchema = BaseInquireResponseSchema.transform(data => {
  let emvTagData = data.emvTagData;
  if (!emvTagData && data.receiptObj && data.receiptObj.emv) {
    emvTagData = data.receiptObj.emv;
  }
  return { ...data, emvTagData };
});


// Strict schema for required fields (approved responses)
const StrictInquireResponseSchema = BaseInquireResponseSchema.safeExtend({
  merchid: z.string().min(1).max(16),
  retref: z.string().min(1).max(32),
  respcode: z.string().min(1).max(3),
  respstat: z.enum(['A', 'B', 'C'])
}).transform(data => {
  let emvTagData = data.emvTagData;
  if (!emvTagData && data.receiptObj && data.receiptObj.emv) {
    emvTagData = data.receiptObj.emv;
  }
  return { ...data, emvTagData };
});

// API response schema (handles string JSON fields)
const ApiInquireResponseSchema = z.object({
  // All fields can come as strings from API
  merchid: z.string().optional(),
  orderid: z.string().optional(),
  expiry: z.string().optional(),
  amount: z.string().optional(),
  currency: z.string().optional(),
  retref: z.string().optional(),
  respcode: z.string().optional(),
  respproc: z.string().optional(),
  respstat: z.string().optional(),
  resptext: z.string().optional(),
  batchid: z.string().optional(),
  setlstat: z.string().optional(),
  capturedate: z.string().optional(),
  settledate: z.string().optional(),
  signature: z.string().optional(),
  token: z.string().optional(),
  account: z.string().optional(),
  authcode: z.union([z.string(), z.number()]).optional(),
  profileid: z.string().optional(),
  acctid: z.string().optional(),
  authdate: z.string().optional(),
  lastfour: z.string().optional(),
  name: z.string().optional(),
  bintype: z.string().optional(),
  entrymode: z.string().optional(),
  cof: z.string().optional(),
  fee_authcode: z.string().optional(),
  fee_format: z.string().optional(),
  fee_merchid: z.string().optional(),
  fee_retref: z.string().optional(),
  fee_type: z.string().optional(),
  fee_value: z.string().optional(),
  emvTagData: z.union([z.string(), z.record(z.string(), z.any())]).optional(),
  receipt: z.union([z.string(), z.record(z.string(), z.any())]).optional(),
  receiptObj: z.union([z.string(), z.record(z.string(), z.any())]).optional(),
  userfields: z.union([z.string(), z.array(z.any()), z.record(z.string(), z.any())]).optional(),
  voidable: z.string().optional(),
  refundable: z.string().optional(),
  declineCategory: z.string().optional(),
  declineCategoryText: z.string().optional(),
  merchAdviceCode: z.string().optional(),
  merchAdviceText: z.string().optional()
})
  .transform((data) => {
    const result: Record<string, any> = {};

    // Copy string fields directly
    const stringFields = [
      'merchid', 'orderid', 'expiry', 'amount', 'currency', 'retref',
      'respcode', 'respproc', 'respstat', 'resptext', 'batchid', 'setlstat',
      'capturedate', 'settledate', 'signature', 'token', 'account', 'authcode',
      'profileid', 'acctid', 'authdate', 'lastfour', 'name', 'bintype', 'entrymode', 'cof',
      'fee_authcode', 'fee_format', 'fee_merchid', 'fee_retref', 'fee_type', 'fee_value',
      'voidable', 'refundable', 'declineCategory', 'declineCategoryText',
      'merchAdviceCode', 'merchAdviceText'
    ];

    stringFields.forEach(field => {
      if ((data as any)[field] !== undefined) {
        result[field] = (data as any)[field];
      }
    });

    // Parse JSON fields
    if (data.emvTagData !== undefined) {
      if (typeof data.emvTagData === 'string') {
        try {
          result.emvTagData = JSON.parse(data.emvTagData);
        } catch {
          result.emvTagData = {};
        }
      } else {
        result.emvTagData = data.emvTagData;
      }
    }

    if (data.receipt !== undefined) {
      if (typeof data.receipt === 'string') {
        try {
          result.receipt = JSON.parse(data.receipt);
        } catch {
          result.receipt = {};
        }
      } else {
        result.receipt = data.receipt;
      }
    }

    if (data.receiptObj !== undefined) {
      if (typeof data.receiptObj === 'string') {
        try {
          result.receiptObj = JSON.parse(data.receiptObj);
        } catch {
          result.receiptObj = {};
        }
      } else {
        result.receiptObj = data.receiptObj;
      }
    }

    if (data.userfields !== undefined) {
      if (typeof data.userfields === 'string') {
        try {
          result.userfields = JSON.parse(data.userfields);
        } catch {
          result.userfields = [];
        }
      } else {
        result.userfields = data.userfields;
      }
    }

    return result;
  });

// Partial schema for updates
const PartialInquireResponseSchema = BaseInquireResponseSchema.partial().transform(data => {
  let emvTagData = data.emvTagData;
  if (!emvTagData && data.receiptObj && data.receiptObj.emv) {
    emvTagData = data.receiptObj.emv;
  }
  return { ...data, emvTagData };
});

// Empty response schema
const EmptyInquireResponseSchema = z.object({}).strict();

// Validation helpers
const validateExpiry = (expiry: string): boolean => {
  return /^\d{4}$/.test(expiry);
};

const validateAuthDate = (authdate: string): boolean => {
  return /^\d{8}$/.test(authdate);
};

const validateDateTime = (datetime: string): boolean => {
  return /^\d{14}$/.test(datetime);
};

const validateAmount = (amount: string): boolean => {
  return /^\d+(\.\d{1,2})?$/.test(amount);
};

// Type exports
export type InquireResponse = z.infer<typeof InquireResponseSchema>;
export type StrictInquireResponse = z.infer<typeof StrictInquireResponseSchema>;
export type ApiInquireResponse = z.infer<typeof ApiInquireResponseSchema>;
export type PartialInquireResponse = z.infer<typeof PartialInquireResponseSchema>;
export type EmptyInquireResponse = z.infer<typeof EmptyInquireResponseSchema>;

// Schema exports
export {
  AmountSchema, ApiInquireResponseSchema, AuthDateSchema, BinTypeSchema, CurrencySchema, DeclineCategorySchema, EmptyInquireResponseSchema, EntryModeSchema, ExpirySchema, FeeFormatSchema, FeeTypeSchema, FullDateTimeSchema, InquireResponseSchema, PartialInquireResponseSchema, RefundableSchema, RespStatSchema, SetlStatSchema, StrictInquireResponseSchema, UserFieldsSchema, validateAmount, validateAuthDate,
  validateDateTime, validateExpiry, VoidableSchema
};

// Default export
export default InquireResponseSchema;
